import type { BetterDocSpecialty } from "@/modules/patients/detail/better-doc/lib/better-doc-sub-specialties"
import {
  de_BetterDocBodyRegionValues,
  de_BetterDocChildAndAdolescentCheckUpValues,
  type de_BetterDocConsultationReason,
  de_BetterDocConsultationReasonValues,
  de_BetterDocExcludingDiagnosisValues,
  de_BetterDocPreventiveCareScreeningValues,
  de_BetterDocPreviousDoctorValues,
  type de_BetterDocSpecificExamination,
  de_BetterDocSpecificExaminationValues,
  type de_BetterDocSymptomLocation,
  de_BetterDocSymptomLocationValues,
} from "@/types/__generated__/union-types"

export const YES_NO_OPTIONS = ["yes", "no"] as const

export const BETTER_DOC_CONSULTATION_QUESTION_IDS = [
  "reasonForConsultations",
  "preventiveCareScreening",
  "childAndAdolescentCheckUpDetails",
  "specificExamination",
  "bodyPartToExamine",
  "allergiesToTest",
  "bodyRegion",
  "subSpecialty",
  "previousDoctor",
  "excludingDiagnosis",
  "symptomLocation",
  "wasMagneticResonanceImagingDone",
  "haveSymptomsLastedLongerThanOneMonth",
] as const

export type BetterDocConsultationQuestionId = (typeof BETTER_DOC_CONSULTATION_QUESTION_IDS)[number]
export type BetterDocConsultationQuestionKind = "select" | "text" | "yesNo"
export type BetterDocConsultationAnswers = Partial<Record<BetterDocConsultationQuestionId, string>>
export type BetterDocConsultationQuestion = {
  id: BetterDocConsultationQuestionId
  kind: BetterDocConsultationQuestionKind
  isRequired: boolean
  dependsOn: readonly BetterDocConsultationQuestionId[]
  isShown: (answers: BetterDocConsultationAnswers, specialtyName: BetterDocSpecialty | undefined) => boolean
}

export const BETTER_DOC_CONSULTATION_SELECT_OPTIONS: Partial<
  Record<BetterDocConsultationQuestionId, readonly string[]>
> = {
  reasonForConsultations: de_BetterDocConsultationReasonValues,
  preventiveCareScreening: de_BetterDocPreventiveCareScreeningValues,
  childAndAdolescentCheckUpDetails: de_BetterDocChildAndAdolescentCheckUpValues,
  specificExamination: de_BetterDocSpecificExaminationValues,
  bodyRegion: de_BetterDocBodyRegionValues,
  previousDoctor: de_BetterDocPreviousDoctorValues,
  excludingDiagnosis: de_BetterDocExcludingDiagnosisValues,
  symptomLocation: de_BetterDocSymptomLocationValues,
}

const ORTHOPEDIC_SPECIALTY: BetterDocSpecialty = "ORTHOPEDIC_SURGERY_AND_TRAUMATOLOGY_OF_THE_MUSCULOSKELETAL_SYSTEM"

const IMAGING_EXAMINATIONS: readonly de_BetterDocSpecificExamination[] = ["CT", "MRI", "X_RAY", "ULTRASOUND"]
const REASONS_SHOWING_BODY_REGION: readonly de_BetterDocConsultationReason[] = ["SURGEON_WANTED", "SECOND_OPINION"]
const LOCATIONS_ASKING_MAGNETIC_RESONANCE_IMAGING_QUESTION: readonly de_BetterDocSymptomLocation[] = [
  "SPINE_AND_BACK",
  "HIP",
  "KNEE",
]
const LOCATIONS_SKIPPING_MAGNETIC_RESONANCE_IMAGING_QUESTION: readonly de_BetterDocSymptomLocation[] = [
  "SHOULDER",
  "ELBOW",
  "HAND_AND_WRIST",
  "FOOT_AND_ANKLE",
]

const isAnswerOneOf = (allowedAnswers: readonly string[], answer: string | undefined) =>
  answer !== undefined && allowedAnswers.some((allowedAnswer) => allowedAnswer === answer)

const isOrthopedicConsultation = (
  answers: BetterDocConsultationAnswers,
  specialtyName: BetterDocSpecialty | undefined,
) => answers.reasonForConsultations !== undefined && specialtyName === ORTHOPEDIC_SPECIALTY

const isLocationAskingMagneticResonanceImagingQuestion = (location: string | undefined) =>
  isAnswerOneOf(LOCATIONS_ASKING_MAGNETIC_RESONANCE_IMAGING_QUESTION, location)

export const BETTER_DOC_CONSULTATION_QUESTIONS: readonly BetterDocConsultationQuestion[] = [
  {
    id: "reasonForConsultations",
    kind: "select",
    isRequired: true,
    dependsOn: [],
    isShown: () => true,
  },
  {
    id: "preventiveCareScreening",
    kind: "select",
    isRequired: true,
    dependsOn: ["reasonForConsultations"],
    isShown: (answers) => answers.reasonForConsultations === "SPECIALIST_PREVENTIVE_CARE",
  },
  {
    id: "childAndAdolescentCheckUpDetails",
    kind: "select",
    isRequired: true,
    dependsOn: ["preventiveCareScreening"],
    isShown: (answers) => answers.preventiveCareScreening === "CHILD_AND_ADOLESCENT_SCREENING",
  },
  {
    id: "specificExamination",
    kind: "select",
    isRequired: true,
    dependsOn: ["reasonForConsultations"],
    isShown: (answers) => answers.reasonForConsultations === "SPECIALIST_DIAGNOSTIC_PROCEDURE",
  },
  {
    id: "bodyPartToExamine",
    kind: "text",
    isRequired: true,
    dependsOn: ["specificExamination"],
    isShown: (answers) => isAnswerOneOf(IMAGING_EXAMINATIONS, answers.specificExamination),
  },
  {
    id: "allergiesToTest",
    kind: "text",
    isRequired: true,
    dependsOn: ["specificExamination"],
    isShown: (answers) => answers.specificExamination === "ALLERGY_TEST",
  },
  {
    id: "bodyRegion",
    kind: "select",
    isRequired: true,
    dependsOn: ["reasonForConsultations"],
    isShown: (answers) => isAnswerOneOf(REASONS_SHOWING_BODY_REGION, answers.reasonForConsultations),
  },
  {
    id: "subSpecialty",
    kind: "select",
    isRequired: false,
    dependsOn: ["reasonForConsultations"],
    isShown: (answers) => answers.reasonForConsultations === "FIRST_SPECIALIST_CONSULTATION",
  },
  {
    id: "previousDoctor",
    kind: "select",
    isRequired: true,
    dependsOn: ["reasonForConsultations"],
    isShown: (answers) => answers.reasonForConsultations === "FIRST_SPECIALIST_CONSULTATION",
  },
  {
    id: "excludingDiagnosis",
    kind: "select",
    isRequired: true,
    dependsOn: ["reasonForConsultations"],
    isShown: (answers) => answers.reasonForConsultations === "FIRST_SPECIALIST_CONSULTATION",
  },
  {
    id: "symptomLocation",
    kind: "select",
    isRequired: true,
    dependsOn: ["bodyRegion", "reasonForConsultations"],
    isShown: (answers, specialtyName) =>
      answers.bodyRegion === "BONES_JOINTS_AND_MUSCULOSKELETAL_SYSTEM" ||
      isOrthopedicConsultation(answers, specialtyName),
  },
  {
    id: "wasMagneticResonanceImagingDone",
    kind: "yesNo",
    isRequired: true,
    dependsOn: ["reasonForConsultations", "symptomLocation"],
    isShown: (answers, specialtyName) =>
      isOrthopedicConsultation(answers, specialtyName) &&
      isLocationAskingMagneticResonanceImagingQuestion(answers.symptomLocation),
  },
  {
    id: "haveSymptomsLastedLongerThanOneMonth",
    kind: "yesNo",
    isRequired: true,
    dependsOn: ["reasonForConsultations", "symptomLocation", "wasMagneticResonanceImagingDone"],
    isShown: (answers, specialtyName) =>
      isOrthopedicConsultation(answers, specialtyName) &&
      ((isLocationAskingMagneticResonanceImagingQuestion(answers.symptomLocation) &&
        answers.wasMagneticResonanceImagingDone === "no") ||
        isAnswerOneOf(LOCATIONS_SKIPPING_MAGNETIC_RESONANCE_IMAGING_QUESTION, answers.symptomLocation)),
  },
]

export const BETTER_DOC_CONSULTATION_QUESTION_IDS_DECIDING_VISIBILITY = BETTER_DOC_CONSULTATION_QUESTION_IDS.filter(
  (questionId) => BETTER_DOC_CONSULTATION_QUESTIONS.some((question) => question.dependsOn.includes(questionId)),
)
